import { AppTranslationPipe } from '@adapter/i18n/app-translation.pipe';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  output,
  signal
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { AppInputOption } from '@helpers/input/input.option';
import { SelectModule } from 'primeng/select';
/**
 * Componente que renderiza un input de tipo dropdown
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-input-dropdown',
  imports: [FormsModule, ReactiveFormsModule, SelectModule, AppTranslationPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputDropdownComponent),
      multi: true
    }
  ],
  template: `
    @if (label()) {
      <label
        class="field-label"
        [class.field-required]="required()"
        [for]="id()"
        >{{ label() | translate }}</label
      >
    }
    <p-select
      [inputId]="id()"
      [options]="items()"
      optionLabel="description"
      [attr.aria-labelledby]="id() + '-label'"
      [showClear]="showClear()"
      [checkmark]="true"
      [loading]="loading()"
      [filter]="filter()"
      [placeholder]="placeholder() | translate"
      filterBy="description"
      styleClass="w-full"
      appendTo="body"
      [disabled]="isDisabled()"
      (onChange)="onItemChange()"
      [(ngModel)]="value"
      (ngModelChange)="updateValue($event)"
      [class.selected]="value()"
    />
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }

    .field-label {
      font-size: 0.85rem;
      font-weight: 600;
      color: #374151;
      padding-left: 0.25rem;
    }

    .field-required::after {
      content: ' *';
      color: #dc2626;
    }

    :host ::ng-deep .p-select.selected {
      background: #f3f4f5;
    }
  `
})
export class AppInputDropdownComponent implements ControlValueAccessor {
  /**
   * Identificador del input
   */
  id = input.required<string>();

  /**
   * Opciones del dropdown
   */
  items = input.required<AppInputOption[]>();

  /**
   * Etiqueta del campo
   */
  label = input<string>('');

  /**
   * Indica si el campo es requerido (agrega asterisco al label)
   */
  required = input<boolean>(false);

  /**
   * Indica si el input está cargando
   */
  loading = input<boolean>(false);

  /**
   * Indica si se muestra el botón de limpiar
   */
  showClear = input<boolean>(true);

  /**
   * Indica si se muestra el input de filtro
   */
  filter = input<boolean>(true);

  /**
   * Placeholder del input (opcional, para usar en lugar de floating label)
   */
  placeholder = input<string>('');

  /**
   * Evento que se emite cuando el valor del input cambia
   */
  changed = output<AppInputOption | undefined>();

  /**
   * Valor del input
   */
  value = signal<AppInputOption | undefined>(undefined);

  /**
   * Indica si el input está deshabilitado
   */
  isDisabled = signal(false);

  onChange?: (value: AppInputOption) => void;

  onTouched?: () => void;

  onItemChange(): void {
    this.changed.emit(this.value());
  }

  writeValue(value: AppInputOption): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: AppInputOption) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  updateValue(event: AppInputOption): void {
    this.value.set(event);
    this.onChange!(event);
    this.onTouched!();
  }
}
